import {
  roleAtLeast,
  TRAFFIC_CAPS,
  TrafficTopDimension,
  type TrafficTopRow,
} from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { lookupAddresses } from '../api/addresses.js';
import { getTrafficTop, type TrafficScope } from '../api/traffic.js';
import { useCurrentUser } from '../auth/session-context.js';
import { blockRuleLink } from '../lib/block-page.js';
import { PageHeader } from '../components/PageHeader.js';
import { botLabel } from '../lib/traffic.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, ShareBar, type Column } from '../ui/DataTable.js';
import { EmptyState } from '../components/EmptyState.js';
import { TrafficRowDetail } from '../components/TrafficRowDetail.js';
import { KeyCell } from '../components/traffic-parts.js';
import { errorsColumn, hasRisk, refusedColumn, riskColumn } from '../components/traffic-columns.js';

/**
 * Traffic explorer: the whole of one top list, searchable and sortable, behind
 * each "View all" on the Traffic page. The scope (server, group, range) comes in
 * the query string, so the filters survive the trip from the dashboard and back.
 *
 * What it can show is what the agents send: capped per-minute top lists, never
 * log lines (rule 19). So a list is at most `TRAFFIC_CAPS[dimension]` long, and
 * the page says so rather than implying a complete log.
 */

const TITLES: Readonly<Partial<Record<TrafficTopDimension, [string, string]>>> = {
  bot: ['Bots', 'Bot'],
  ua_family: ['Browsers and clients', 'Client'],
  path: ['Pages', 'Page'],
  ip: ['Source addresses', 'Address'],
  status: ['Status codes', 'Status'],
  host: ['Sites', 'Site'],
  category: ['Visitor types', 'Type'],
};

const PAGE_SIZE = 25;
const count = (value: number): string => Math.round(value).toLocaleString('en-US');

export function TrafficExplorerPage(): ReactNode {
  const session = useCurrentUser();
  const parsed = TrafficTopDimension.safeParse(useParams()['dimension']);
  const [params] = useSearchParams();
  // A link from an address's page arrives with `?q=`, already narrowed to it.
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [page, setPage] = useState(0);

  const serverId = params.get('server_id') ?? '';
  const groupId = params.get('group_id') ?? '';
  const hours = Number(params.get('hours') ?? '24');
  const from = useMemo(() => new Date(Date.now() - hours * 3_600_000).toISOString(), [hours]);
  const scope: TrafficScope = {
    ...(serverId === '' ? {} : { serverId }),
    ...(groupId === '' || serverId !== '' ? {} : { groupId }),
    from,
  };
  const dimension = parsed.success ? parsed.data : 'ip';

  const top = useQuery({
    queryKey: ['traffic-top', scope, dimension, 'explorer'],
    queryFn: () => getTrafficTop(scope, dimension, TRAFFIC_CAPS[dimension]),
    placeholderData: keepPreviousData,
    enabled: parsed.success,
  });
  const rows = top.data?.rows ?? [];
  const ips = dimension === 'ip' ? rows.map((row) => row.key) : [];
  const statuses = useQuery({
    queryKey: ['address-lookup', ips],
    queryFn: () => lookupAddresses(ips),
    enabled: ips.length > 0,
  });
  const statusOf = new Map((statuses.data ?? []).map((entry) => [entry.ip, entry]));

  if (!parsed.success) {
    return (
      <div className="page">
        <Alert tone="warning" title="Unknown list">
          <Link to="/traffic">Back to Traffic</Link>
        </Alert>
      </div>
    );
  }

  const [title, noun] = TITLES[dimension] ?? ['Traffic', 'Name'];
  const needle = query.trim().toLowerCase();
  const shown = rows.filter(
    (row) =>
      needle === '' ||
      row.key.toLowerCase().includes(needle) ||
      (dimension === 'bot' && botLabel(row.key).toLowerCase().includes(needle)),
  );
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = shown.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const total = rows.reduce((sum, row) => sum + row.requests, 0);

  const columns: Column<TrafficTopRow>[] = [
    {
      key: 'key',
      header: noun,
      rowHeader: true,
      sortValue: (row) => row.key,
      cell: (row) => <KeyCell dimension={dimension} row={row} status={statusOf.get(row.key)} />,
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'end',
      sortValue: (row) => row.requests,
      cell: (row) => <ShareBar value={row.requests} total={total} />,
    },
    refusedColumn(),
    errorsColumn(),
    ...(hasRisk(dimension) ? [riskColumn()] : []),
    {
      key: 'ms',
      header: 'Avg response',
      align: 'end',
      wideOnly: true,
      sortValue: (row) => row.avgResponseMs,
      cell: (row) => `${count(row.avgResponseMs)} ms`,
    },
  ];

  return (
    <div className="page">
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <Link to={`/traffic${window.location.search}`}>Traffic</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{title}</span>
      </nav>
      <PageHeader
        title={title}
        description="Everything the agents reported for this list in the chosen range, from the same filters as the Traffic page."
      />
      <div className="filters">
        <label className="field">
          <span className="field-label">Search</span>
          <input
            type="search"
            value={query}
            placeholder={`Search ${noun.toLowerCase()}…`}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
          />
        </label>
      </div>
      {top.isError && (
        <Alert tone="danger" title="Could not load this list">
          Try again in a moment.
        </Alert>
      )}
      <Card
        title={title}
        description={`${count(shown.length)} of the top ${count(TRAFFIC_CAPS[dimension])} by requests. Agents send capped summaries, never log lines.`}
      >
        {top.isPending ? (
          <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading the list" />
        ) : (
          <DataTable
            caption={`${title}, by requests`}
            columns={columns}
            rows={visible}
            rowKey={(row) => row.key}
            renderDetail={(row) => (
              <TrafficRowDetail
                dimension={dimension}
                row={row}
                scope={scope}
                blockPage={
                  roleAtLeast(session.user.role, 'operator')
                    ? (path) =>
                        blockRuleLink(path, { serverId: scope.serverId, groupId: scope.groupId })
                    : undefined
                }
              />
            )}
            initialSort={{ key: 'requests', direction: 'desc' }}
            empty={
              <EmptyState title="Nothing found">
                Try another search, or widen the time range on the Traffic page.
              </EmptyState>
            }
          />
        )}
        {pages > 1 && (
          <div className="row text-sm">
            <Button
              disabled={current === 0}
              onClick={() => {
                setPage(current - 1);
              }}
            >
              Previous
            </Button>
            <span className="muted">
              Page {current + 1} of {pages}
            </span>
            <Button
              disabled={current >= pages - 1}
              onClick={() => {
                setPage(current + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
